import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdConfigs,
  addAdConfig,
  updateAdConfig,
  changeAdConfigStatus
} from "../../../redux/slice/adConfigSlice";
import { PlusIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import StatusChip from "../../../components/statusChip/StatusChip";
import AddEditAdConfigModal from "./AddEditAdConfigModal";
import { usePermission } from "../../../hooks/usePermission";
import { useToast } from "../../../context/TostContext";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";

const MODULE_SLUG = "ad_config";

const AdConfigList = () => {
  const dispatch = useDispatch();
  const { adConfigs, isLoading, updateLoading } = useSelector((state) => state.adConfig);
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState({ value: 10, label: 10 });
  const [showModal, setShowModal] = useState(false);
  const [isView, setIsView] = useState(false);
  const [selectedAdConfig, setSelectedAdConfig] = useState(null);

  useEffect(() => {
    dispatch(fetchAdConfigs({ search: searchTerm, page: currentPage, limit: limit?.value || 10 }));
  }, [dispatch, searchTerm, currentPage, limit]);

  const handleToggleStatus = useCallback((item) => {
    dispatch(changeAdConfigStatus({ id: item.id, is_active: !item.is_active }))
      .unwrap()
      .then(() => toast.success("Status updated successfully"))
      .catch((err) => toast.error(err || "Failed to update status"));
  }, [dispatch, toast]);

  const handleEdit = (item) => {
    setSelectedAdConfig(item);
    setIsView(false);
    setShowModal(true);
  };

  const handleView = (item) => {
    setSelectedAdConfig(item);
    setIsView(true);
    setShowModal(true);
  };

  const handleAdd = (data) => {
    if (selectedAdConfig) {
      dispatch(updateAdConfig({ id: selectedAdConfig.id, data }))
        .unwrap()
        .then(() => {
          toast.success("Updated successfully");
          setShowModal(false);
        })
        .catch((err) => toast.error(err || "Failed to update"));
    } else {
      dispatch(addAdConfig(data))
        .unwrap()
        .then(() => {
          toast.success("Added successfully");
          setShowModal(false);
        })
        .catch((err) => toast.error(err || "Failed to add"));
    }
  };

  const columns = useMemo(() => [
    {
      id: "screen",
      header: "Screen",
      cell: (_, row) => row.screen || "-",
      className: "fw-medium"
    },
    {
      id: "screen_key",
      header: "Key",
      cell: (_, row) => row.screen_key || "-",
      className: "text-muted small"
    },
    {
      id: "ad_type",
      header: "Type",
      cell: (_, row) => row.ad_type || "-",
      className: "text-capitalize"
    },
    {
      id: "provider",
      header: "Provider",
      cell: (_, row) => row.provider || "-",
      className: "text-uppercase small"
    },
    {
      id: "status",
      header: "Status",
      cell: (_, row) => (
        <StatusChip
          bg={row.is_active ? "success" : "danger"}
          title={row.is_active ? "Active" : "Inactive"}
          pill
          className={canUpdate ? "cursor-pointer" : ""}
          onClick={() => {
            if (canUpdate) handleToggleStatus(row);
          }}
        />
      )
    }
  ], [canUpdate, handleToggleStatus]);

  const paginationConfig = useMemo(() => {
    const total = adConfigs?.pagination?.total || 0;
    const pageSize = limit?.value || 10;
    const totalPages = Math.ceil(total / pageSize);
    return {
      total,
      totalPages,
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize: (size) => {
        setLimit({ value: size, label: size });
        setCurrentPage(1);
      },
      onPageChange: (page) => setCurrentPage(page),
    };
  }, [adConfigs?.pagination?.total, currentPage, limit]);

  const actionList = useMemo(() => [
    { title: "View", handler: (row) => handleView(row) },
    canUpdate && { title: "Edit", handler: (row) => handleEdit(row) },
    canUpdate && {
      title: (row) => row.is_active ? "Deactivate" : "Activate",
      handler: (row) => handleToggleStatus(row)
    }
  ].filter(Boolean), [canUpdate, handleToggleStatus]);

  return (
    <div className="bg-white rounded-3 shadow-sm overflow-hidden">
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch value={searchTerm}
          placeholder="Search Screen or Key"
          onChange={(e) => setSearchTerm(e.target.value)} />
        {canCreate && (
          <CommonButton
            title="Add New"
            icon={<PlusIcon width={18} height={18} />}
            onClick={() => {
              setSelectedAdConfig(null);
              setIsView(false);
              setShowModal(true);
            }}
          />
        )}
      </div>

      <CustomTable
        data={adConfigs?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="No ad configs found"
        keyField="id"
        actionList={actionList}
        pagination={paginationConfig}
      />

      <AddEditAdConfigModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isView ? "View Ad Config" : selectedAdConfig ? "Edit Ad Config" : "Add Ad Config"}
        size="lg"
        onSubmit={handleAdd}
        adConfig={selectedAdConfig}
        isLoading={updateLoading}
        isView={isView}
      />
    </div>
  );
};

export default AdConfigList;
